/**
 * Sentry Observability & Error Reporting for Vite Admin
 *
 * Provides safe exception capture and APM tracing with graceful fallback
 * when VITE_SENTRY_DSN is not configured or offline.
 */
import * as Sentry from '@sentry/react';

export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';

export interface SentryContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  user?: { id?: string; email?: string };
  [key: string]: unknown;
}

let isInitialized = false;

export function initSentry(): void {
  if (isInitialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const env = import.meta.env.MODE || 'development';

  if (dsn) {
    Sentry.init({
      dsn,
      environment: env,
      tracesSampleRate: env === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event) {
        // Redact sensitive credentials and tokens before sending
        if (event.request) {
          delete event.request.cookies;
          if (event.request.headers) {
            delete event.request.headers['authorization'];
            delete event.request.headers['cookie'];
          }
        }
        return event;
      },
    });
  }

  isInitialized = true;
}

export function captureException(error: unknown, context?: SentryContext): string {
  const eventId = `admin_err_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn) {
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra,
      user: context?.user,
    });
  }

  if (import.meta.env.DEV) {
    console.error('[Sentry:Admin] Captured exception:', error, { eventId, ...context });
  }

  return eventId;
}

export function captureMessage(
  message: string,
  level: SeverityLevel = 'info',
  context?: SentryContext
): string {
  const eventId = `admin_msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn) {
    Sentry.captureMessage(message, {
      level:
        level === 'fatal'
          ? 'fatal'
          : level === 'error'
            ? 'error'
            : level === 'warning'
              ? 'warning'
              : 'info',
      tags: context?.tags,
      extra: context?.extra,
      user: context?.user,
    });
  }

  if (import.meta.env.DEV) {
    console.log(`[Sentry:Admin] [${level}] ${message}`, { eventId, ...context });
  }

  return eventId;
}

export function setSentryUser(user: { id?: string; email?: string } | null): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn) {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }

  if (import.meta.env.DEV) {
    console.log(
      '[Sentry:Admin] Set user context:',
      user ? { id: user.id, email: user.email } : null
    );
  }
}
